import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, timeAgo } from './ui/Kit';

export const POLL_MS = 30000;

// Where a notification takes you: the job page (/jobs/:id). Older links ("/dashboard/user", /view-requirement/:id)
// are mapped there too (role kept for callers).
export function notificationTarget(n, role) {
  const link = n.link || '';
  if (!link || link === '/dashboard/user') return n.requirement_id ? `/jobs/${n.requirement_id}` : '/jobs';
  const old = link.match(/^\/view-requirement\/(\d+)/);
  return old ? `/jobs/${old[1]}` : link;
}

function storedRole() {
  try { return (JSON.parse(localStorage.getItem('user') || '{}') || {}).role; } catch (e) { return undefined; }
}

// The yellow bell in the top bar: unread count (polls every 30 s), a panel with All / Unread, Mark all read and
// Clear all; a notification opens its job; "View all notifications →" opens the full list + preferences.
export default function NotificationBell() {
  const navigate = useNavigate();
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState('all');
  const [items, setItems] = useState(null);
  const [error, setError] = useState('');
  const box = useRef(null);
  const button = useRef(null);

  const poll = useCallback(async () => {
    try { setUnread((await api('/api/notifications/unread-count')).unread || 0); } catch (e) { /* offline / logged out */ }
  }, []);
  const load = useCallback(async (which) => {
    setError('');
    try {
      const r = await api(`/api/notifications?filter=${which}&page_size=10`);
      setItems(r.items); setUnread(r.unread);
    } catch (e) { setError(e.message); setItems([]); }
  }, []);

  useEffect(() => {
    poll();
    const t = setInterval(poll, POLL_MS);
    return () => clearInterval(t);
  }, [poll]);
  useEffect(() => { if (open) load(tab); }, [open, tab, load]);
  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => { if (box.current && !box.current.contains(e.target)) setOpen(false); };
    const onKey = (e) => { if (e.key === 'Escape') { setOpen(false); if (button.current) button.current.focus(); } };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onDoc); document.removeEventListener('keydown', onKey); };
  }, [open]);

  const openItem = async (n) => {
    try { if (!n.is_read) await api(`/api/notifications/${n.id}/mark-read`, { method: 'POST' }); } catch (e) { /* ignore */ }
    setOpen(false);
    poll();
    navigate(notificationTarget(n, storedRole()));
  };
  const markAll = async () => { try { await api('/api/notifications/read-all', { method: 'POST' }); await load(tab); } catch (e) { setError(e.message); } };
  const clearAll = async () => { try { await api('/api/notifications/clear-all', { method: 'POST' }); await load(tab); } catch (e) { setError(e.message); } };

  return (
    <div className="relative font-inter" ref={box}>
      <button ref={button} onClick={() => setOpen(!open)} aria-haspopup="dialog" aria-expanded={open}
        aria-label={unread ? `Notifications, ${unread} unread` : 'Notifications'}
        className="relative w-10 h-10 rounded-full flex items-center justify-center hover:bg-[#F5F7FB] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#0066CC]/40">
        <svg className="w-6 h-6" viewBox="0 0 24 24" fill="#FACC15" stroke="#CA8A04" strokeWidth="1.5" aria-hidden="true">
          <path d="M12 3a6 6 0 00-6 6v3.6l-1.6 2.7A1 1 0 005.3 17h13.4a1 1 0 00.9-1.7L18 12.6V9a6 6 0 00-6-6z" />
          <path d="M9.5 19a2.5 2.5 0 005 0" fill="none" />
        </svg>
        {unread > 0 && (
          <span data-testid="bell-count" className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-red-600 text-white text-[10px] font-bold flex items-center justify-center">
            {unread > 99 ? '99+' : unread}
          </span>
        )}
      </button>
      {open && (
        <div role="dialog" aria-label="Notifications" className="absolute right-0 mt-2 w-[380px] bg-white border border-[#E6E9F0] rounded-[12px] shadow-2xl z-50">
          <div className="flex items-center justify-between px-4 pt-4 pb-2">
            <p className="text-sm font-extrabold text-[#0F172A]">Notifications{unread ? <span className="text-[#64748B] font-semibold"> · {unread} new</span> : null}</p>
            <div className="flex gap-3 text-xs font-semibold">
              <button onClick={markAll} className="text-[#0066CC] hover:underline">Mark all read</button>
              <button onClick={clearAll} className="text-[#64748B] hover:underline">Clear all</button>
            </div>
          </div>
          <div role="tablist" aria-label="Show" className="flex gap-4 px-4 border-b border-[#E6E9F0]">
            {[['all', 'All'], ['unread', `Unread${unread ? ` (${unread})` : ''}`]].map(([k, lbl]) => (
              <button key={k} role="tab" aria-selected={tab === k} onClick={() => setTab(k)}
                className={`-mb-px pb-2 text-xs font-semibold border-b-2 ${tab === k ? 'border-[#0066CC] text-[#0066CC]' : 'border-transparent text-[#64748B]'}`}>{lbl}</button>
            ))}
          </div>
          <ul className="max-h-[360px] overflow-y-auto">
            {items === null && <li className="px-4 py-6 text-sm text-[#64748B]">Loading…</li>}
            {error && <li role="alert" className="px-4 py-3 text-sm text-red-700">{error}</li>}
            {items && !items.length && !error && (
              <li className="px-4 py-8 text-center text-sm text-[#64748B]">{tab === 'unread' ? 'Nothing unread.' : "You're all caught up."}</li>
            )}
            {(items || []).map((n) => (
              <li key={n.id}>
                <button onClick={() => openItem(n)} className={`w-full text-left px-4 py-3 border-b border-[#F1F4F8] hover:bg-[#F5F7FB] flex gap-3 ${n.is_read ? '' : 'bg-[#F7FAFE]'}`}>
                  <span aria-hidden="true" className={`mt-1.5 w-2 h-2 rounded-full shrink-0 ${n.is_read ? 'bg-transparent' : 'bg-[#0066CC]'}`} />
                  <span className="min-w-0">
                    <span className="block text-sm font-semibold text-[#0F172A] truncate">{n.title}</span>
                    {n.message && <span className="block text-xs text-[#64748B] line-clamp-2">{n.message}</span>}
                    <span className="block text-[11px] text-[#94A3B8] mt-0.5">{timeAgo(n.created_at)}{n.is_read ? '' : <span className="sr-only"> · unread</span>}</span>
                  </span>
                </button>
              </li>
            ))}
          </ul>
          <button onClick={() => { setOpen(false); navigate('/notifications'); }} className="w-full px-4 py-3 text-sm font-semibold text-[#0066CC] hover:bg-[#F5F7FB] rounded-b-[12px] text-left">
            View all notifications →
          </button>
        </div>
      )}
    </div>
  );
}
